import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Dialog, DialogContent,
  TextField, Grid, Alert, TableSortLabel, Menu,
  MenuItem, Select, FormControl, InputLabel, Chip,
  Card, CardContent, useTheme, useMediaQuery,
  Checkbox, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Category as CategoryIcon,
  AutoFixHigh as AutoFixHighIcon,
  Upload as UploadIcon,
  LocationOn as LocationOnIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useColumnSettings } from '../hooks/useColumnSettings';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState('');
  const stickyHeaderRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(64);

  useEffect(() => {
    const measure = () => {
      if (stickyHeaderRef.current) {
        const rect = stickyHeaderRef.current.getBoundingClientRect();
        setStickyHeaderHeight(rect.bottom);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    const interval = setInterval(measure, 500);
    return () => {
      window.removeEventListener('resize', measure);
      clearInterval(interval);
    };
  }, []);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [orderBy, setOrderBy] = useState('asset_number');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportCategory, setExportCategory] = useState('all');
  const [exportType, setExportType] = useState('summary');
  const [openManageCategories, setOpenManageCategories] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [openManageLocations, setOpenManageLocations] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [openRenumberDialog, setOpenRenumberDialog] = useState(false);
  const [renumberMode, setRenumberMode] = useState('sequential');
  const [renumberStart, setRenumberStart] = useState('');
  const [renumberPrefix, setRenumberPrefix] = useState('');
  const [renumberSuffix, setRenumberSuffix] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [renumberPreview, setRenumberPreview] = useState([]);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState('');
  const [importMode, setImportMode] = useState('new');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  // Inline editing state (desktop only)
  const [editingCell, setEditingCell] = useState(null); // { assetId, field }
  const [editingValue, setEditingValue] = useState('');
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    borderColor,
    tableRowHover,
    tableRowAlt,
    pmTypeBackground,
    pmTypeText,
    paperBackground,
    pageBackground,
    overdueColors,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  // Column settings hook
  const defaultColumns = ['asset_number', 'asset_description', 'category', 'location', 'manufacturer', 'model', 'serial_number', 'installation_date', 'department', 'current_meter_reading', 'last_maintenance_date'];
  const { isColumnVisible } = useColumnSettings('assets', defaultColumns);

  useEffect(() => {
    loadAssets();
    loadLocations();
  }, []);

  const loadAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (err) {
      setError('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset? This will also delete all related work orders and PM schedules.')) {
      return;
    }
    try {
      await api.delete(`/assets/${id}`);
      loadAssets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete asset');
    }
  };

  // Inline editing handlers (desktop only)
  const handleInlineEditStart = (assetId, field, currentValue, event) => {
    event.stopPropagation(); // Prevent row click navigation
    setEditingCell({ assetId, field });
    setEditingValue(currentValue || '');
  };

  const handleInlineEditSave = async () => {
    if (!editingCell) return;

    const { assetId, field } = editingCell;
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return;

    // Only save if value changed
    if (editingValue !== (asset[field] || '')) {
      try {
        await api.patch(`/assets/${assetId}`, { [field]: editingValue });
        loadAssets();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to update asset');
      }
    }

    setEditingCell(null);
    setEditingValue('');
  };

  const handleInlineEditCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const editableFields = ['manufacturer', 'model', 'serial_number'];

  const handleInlineEditKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleInlineEditSave();
    } else if (event.key === 'Escape') {
      handleInlineEditCancel();
    } else if (event.key === 'Tab' && editingCell) {
      event.preventDefault();
      const { assetId, field } = editingCell;
      const currentIndex = editableFields.indexOf(field);
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = currentIndex + direction;

      // Save current cell first
      const asset = assets.find(a => a.id === assetId);
      if (asset && editingValue !== (asset[field] || '')) {
        api.patch(`/assets/${assetId}`, { [field]: editingValue }).then(() => loadAssets()).catch(() => {});
      }

      if (nextIndex >= 0 && nextIndex < editableFields.length) {
        // Move to next/prev field in same row
        const nextField = editableFields[nextIndex];
        const nextValue = asset ? (asset[nextField] || '') : '';
        setEditingCell({ assetId, field: nextField });
        setEditingValue(nextValue);
      } else {
        // At the end/start of row, close editing
        setEditingCell(null);
        setEditingValue('');
      }
    }
  };

  // Render editable cell (desktop only)
  const renderEditableCell = (asset, field) => {
    const isEditing = editingCell?.assetId === asset.id && editingCell?.field === field;
    const value = asset[field] || '';

    if (isEditing) {
      return (
        <TextField
          size="small"
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={handleInlineEditSave}
          onKeyDown={handleInlineEditKeyDown}
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              fontSize: '0.875rem',
            },
            '& .MuiOutlinedInput-input': {
              py: 0.5,
              px: 1,
            },
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    // Desktop: show clickable cell with hover effect
    if (!isMobile && hasRole('administrator', 'supervisor')) {
      return (
        <Box
          onClick={(e) => handleInlineEditStart(asset.id, field, value, e)}
          sx={{
            cursor: 'text',
            py: 0.5,
            px: 1,
            mx: -1,
            borderRadius: 1,
            minHeight: '24px',
            '&:hover': {
              backgroundColor: 'action.hover',
              outline: `1px dashed ${borderColor}`,
            },
          }}
        >
          {value || '-'}
        </Box>
      );
    }

    // Mobile or non-admin: just show value
    return value || '-';
  };

  const handleSave = async () => {
    if (!formData.asset_number || !formData.asset_description) {
      setError('Asset Number and Description are required');
      return;
    }

    try {
      if (formData.id) {
        await api.put(`/assets/${formData.id}`, formData);
      } else {
        await api.post('/assets', formData);
      }
      setOpenDialog(false);
      setFormData({});
      setError('');
      loadAssets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save asset');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = sortedAssets.map(asset => asset.id);
      setSelectedAssets(allIds);
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (id) => {
    setSelectedAssets(prev => {
      if (prev.includes(id)) {
        return prev.filter(assetId => assetId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleBulkDelete = () => {
    setBulkAction('delete');
    setOpenBulkDialog(true);
  };

  const handleBulkChangeLocation = () => {
    setBulkAction('location');
    setBulkLocation('');
    setOpenBulkDialog(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        await api.post('/assets/bulk-delete', { ids: selectedAssets });
        setError('');
      } else if (bulkAction === 'location') {
        if (!bulkLocation) {
          setError('Please select a location');
          return;
        }
        await api.post('/assets/bulk-update-location', { ids: selectedAssets, location: bulkLocation });
        setError('');
      }
      setOpenBulkDialog(false);
      setSelectedAssets([]);
      loadAssets();
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk operation failed');
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleExportToExcel = () => {
    let assetsToExport = assets;
    if (exportCategory !== 'all') {
      assetsToExport = assets.filter(asset => asset.category === exportCategory);
    }

    let columns;
    if (exportType === 'summary') {
      columns = defaultColumns.filter(col => isColumnVisible(col));
    } else {
      columns = [
        'asset_number', 'asset_description', 'category', 'location',
        'manufacturer', 'model', 'serial_number', 'installation_date',
        'department', 'current_meter_reading', 'last_maintenance_date'
      ];
    }

    const headers = columns.map(col =>
      col.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    );

    const data = assetsToExport.map(asset =>
      columns.map(col => asset[col] || '')
    );

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const colWidths = columns.map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    const sheetName = exportCategory === 'all' ? 'All Assets' : exportCategory;
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const date = new Date().toISOString().split('T')[0];
    const categoryName = exportCategory === 'all' ? 'All' : exportCategory.replace(/\s+/g, '_');
    const fileName = `Assets_${categoryName}_${exportType}_${date}.xlsx`;

    XLSX.writeFile(wb, fileName);
    setOpenExportDialog(false);
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    if (uniqueCategories.includes(newCategory.trim())) {
      setError('Category already exists');
      return;
    }
    setNewCategory('');
    loadAssets();
  };

  const handleRenameCategory = async (oldName, newName) => {
    if (!newName.trim() || oldName === newName) return;

    try {
      const assetsToUpdate = assets.filter(asset => asset.category === oldName);
      for (const asset of assetsToUpdate) {
        await api.put(`/assets/${asset.id}`, { ...asset, category: newName });
      }
      setEditingCategory(null);
      loadAssets();
    } catch (err) {
      setError('Failed to rename category');
      console.error(err);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    try {
      const assetsToUpdate = assets.filter(asset => asset.category === categoryName);
      for (const asset of assetsToUpdate) {
        await api.put(`/assets/${asset.id}`, { ...asset, category: '' });
      }
      setCategoryToDelete(null);
      loadAssets();
    } catch (err) {
      setError('Failed to delete category');
      console.error(err);
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.trim()) return;

    try {
      await api.post('/locations', { name: newLocation.trim() });
      setNewLocation('');
      setError('');
      loadLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add location');
    }
  };

  const handleRenameLocation = async (locationId, newName) => {
    if (!newName.trim()) return;

    try {
      await api.put(`/locations/${locationId}`, { name: newName.trim() });
      setEditingLocation(null);
      setError('');
      loadLocations();
      loadAssets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename location');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await api.delete(`/locations/${locationId}`);
      setLocationToDelete(null);
      setError('');
      loadLocations();
      loadAssets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete location');
    }
  };

  const generateRenumberPreview = () => {
    const assetsToRenumber = sortedAssets.slice(0, 10);
    const preview = [];

    assetsToRenumber.forEach((asset, index) => {
      let newNumber = asset.asset_number;

      if (renumberMode === 'sequential') {
        const startNum = parseInt(renumberStart) || 1;
        newNumber = `${renumberPrefix}${startNum + index}${renumberSuffix}`;
      } else if (renumberMode === 'find_replace') {
        newNumber = asset.asset_number.replace(new RegExp(findText, 'g'), replaceText);
      } else if (renumberMode === 'prefix_suffix') {
        newNumber = `${renumberPrefix}${asset.asset_number}${renumberSuffix}`;
      }

      preview.push({
        id: asset.id,
        oldNumber: asset.asset_number,
        newNumber: newNumber,
        description: asset.asset_description
      });
    });

    setRenumberPreview(preview);
  };

  const handleApplyRenumbering = async () => {
    if (!window.confirm(`Are you sure you want to renumber ${sortedAssets.length} assets? This cannot be undone.`)) {
      return;
    }

    try {
      const updates = [];

      sortedAssets.forEach((asset, index) => {
        let newNumber = asset.asset_number;

        if (renumberMode === 'sequential') {
          const startNum = parseInt(renumberStart) || 1;
          newNumber = `${renumberPrefix}${startNum + index}${renumberSuffix}`;
        } else if (renumberMode === 'find_replace') {
          newNumber = asset.asset_number.replace(new RegExp(findText, 'g'), replaceText);
        } else if (renumberMode === 'prefix_suffix') {
          newNumber = `${renumberPrefix}${asset.asset_number}${renumberSuffix}`;
        }

        if (newNumber !== asset.asset_number) {
          updates.push({ id: asset.id, asset_number: newNumber });
        }
      });

      for (const update of updates) {
        await api.put(`/assets/${update.id}`, { asset_number: update.asset_number });
      }

      setOpenRenumberDialog(false);
      setRenumberMode('sequential');
      setRenumberStart('');
      setRenumberPrefix('');
      setRenumberSuffix('');
      setFindText('');
      setReplaceText('');
      setRenumberPreview([]);
      loadAssets();
      alert(`Successfully renumbered ${updates.length} assets!`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to renumber assets');
      console.error(err);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          setImportError('The file is empty');
          return;
        }

        const firstRow = jsonData[0];

        if (importMode === 'renumber') {
          const hasOldNumber = 'old_asset_number' in firstRow || 'Old Asset Number' in firstRow;
          const hasNewNumber = 'new_asset_number' in firstRow || 'New Asset Number' in firstRow;

          if (!hasOldNumber || !hasNewNumber) {
            setImportError('File must contain columns: "old_asset_number" and "new_asset_number" (or "Old Asset Number" and "New Asset Number")');
            return;
          }

          const updates = jsonData.map(row => {
            const oldNumber = row.old_asset_number || row['Old Asset Number'];
            const newNumber = row.new_asset_number || row['New Asset Number'];
            const asset = assets.find(a => a.asset_number === oldNumber);

            return {
              oldNumber,
              newNumber,
              found: !!asset,
              assetId: asset?.id,
              description: asset?.asset_description || 'Not found'
            };
          });

          setImportPreview(updates);
        } else {
          const hasAssetNumber = 'asset_number' in firstRow || 'Asset Number' in firstRow;
          const hasAssetDescription = 'asset_description' in firstRow || 'Asset Description' in firstRow || 'Asset Name' in firstRow;

          if (!hasAssetNumber || !hasAssetDescription) {
            setImportError('File must contain columns: "Asset Number" and "Asset Description" (or "Asset Name")');
            return;
          }

          const assetNumberCounts = {};
          jsonData.forEach(row => {
            const assetNumber = row.asset_number || row['Asset Number'];
            if (assetNumber) {
              assetNumberCounts[assetNumber] = (assetNumberCounts[assetNumber] || 0) + 1;
            }
          });

          const newAssets = jsonData.map(row => {
            const assetNumber = row.asset_number || row['Asset Number'];
            const assetDescription = row.asset_description || row['Asset Description'] || row['Asset Name'];
            const existsInDb = assets.find(a => a.asset_number === assetNumber);
            const isDuplicateInFile = assetNumberCounts[assetNumber] > 1;

            return {
              asset_number: assetNumber,
              asset_description: assetDescription,
              category: row.category || row.Category || '',
              location: row.location || row.Location || null,
              manufacturer: row.manufacturer || row.Manufacturer || '',
              model: row.model || row.Model || '',
              serial_number: row.serial_number || row['Serial Number'] || '',
              department: row.department || row.Department || '',
              installation_date: row.installation_date || row['Installation Date'] || null,
              purchase_date: row.purchase_date || row['Purchase Date'] || null,
              exists: !!existsInDb,
              duplicateInFile: isDuplicateInFile,
              valid: !!(assetNumber && assetDescription)
            };
          });

          setImportPreview(newAssets);
        }
      } catch (err) {
        setImportError('Failed to read file. Please ensure it is a valid Excel or CSV file.');
        console.error(err);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleApplyImport = async () => {
    if (importMode === 'renumber') {
      const validUpdates = importPreview.filter(u => u.found);

      if (validUpdates.length === 0) {
        setImportError('No valid assets to update');
        return;
      }

      if (!window.confirm(`Are you sure you want to update ${validUpdates.length} assets? This cannot be undone.`)) {
        return;
      }

      try {
        for (const update of validUpdates) {
          await api.put(`/assets/${update.assetId}`, { asset_number: update.newNumber });
        }

        setOpenImportDialog(false);
        setImportFile(null);
        setImportPreview([]);
        setImportError('');
        loadAssets();
        alert(`Successfully updated ${validUpdates.length} assets!`);
      } catch (err) {
        setImportError(err.response?.data?.error || 'Failed to update assets');
        console.error(err);
      }
    } else {
      const validAssets = importPreview.filter(a => a.valid && !a.exists && !a.duplicateInFile);

      if (validAssets.length === 0) {
        setImportError('No valid new assets to import (all assets either already exist, are duplicates in the file, or are missing required fields)');
        return;
      }

      const existingCount = importPreview.filter(a => a.exists).length;
      const duplicateCount = importPreview.filter(a => a.duplicateInFile).length;
      let confirmMessage = `Import ${validAssets.length} new asset${validAssets.length > 1 ? 's' : ''}?`;
      if (existingCount > 0) {
        confirmMessage += `\n\n${existingCount} asset${existingCount > 1 ? 's' : ''} will be skipped (already exist in database).`;
      }
      if (duplicateCount > 0) {
        confirmMessage += `\n\n${duplicateCount} row${duplicateCount > 1 ? 's' : ''} will be skipped (duplicate asset numbers in file).`;
      }

      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        let importedCount = 0;
        const failedAssets = [];

        for (const asset of validAssets) {
          const { exists, valid, duplicateInFile, ...assetData } = asset;
          try {
            await api.post('/assets', assetData);
            importedCount++;
          } catch (assetErr) {
            failedAssets.push({
              asset_number: asset.asset_number,
              error: assetErr.response?.data?.error || 'Unknown error'
            });
          }
        }

        if (failedAssets.length > 0) {
          const failedList = failedAssets.map(f => `${f.asset_number}: ${f.error}`).join('\n');
          setImportError(`Failed to import ${failedAssets.length} asset(s):\n${failedList}`);
          if (importedCount > 0) {
            loadAssets();
            alert(`Imported ${importedCount} asset(s), but ${failedAssets.length} failed. See error details below.`);
          }
        } else {
          setOpenImportDialog(false);
          setImportFile(null);
          setImportPreview([]);
          setImportError('');
          loadAssets();
          alert(`Successfully imported ${importedCount} new asset${importedCount > 1 ? 's' : ''}!`);
        }
      } catch (err) {
        setImportError(err.response?.data?.error || 'Failed to import assets');
        console.error(err);
      }
    }
  };

  const handleDownloadSampleTemplate = () => {
    const columns = [
      'Asset Number',
      'Asset Description',
      'Category',
      'Location',
      'Manufacturer',
      'Model',
      'Serial Number',
      'Department',
      'Installation Date',
      'Purchase Date'
    ];

    const sampleData = [
      ['A-001', 'Hydraulic Press', 'Manufacturing Equipment', 'Warehouse', 'ACME Corp', 'HP-2000', 'SN123456', 'Production', '2024-01-15', '2024-01-01'],
      ['A-002', 'Conveyor Belt System', 'Material Handling', 'Warehouse', 'ConveyTech', 'CB-500', 'SN789012', 'Production', '2024-02-20', '2024-02-01'],
      ['A-003', 'Desktop Computer', 'IT Equipment', 'Office', 'Dell', 'OptiPlex 7090', 'DL345678', 'IT Department', '2024-03-10', '2024-03-01'],
    ];

    const ws = XLSX.utils.aoa_to_sheet([columns, ...sampleData]);
    ws['!cols'] = columns.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assets Import Template');
    XLSX.writeFile(wb, 'Assets_Import_Template.xlsx');
  };

  const uniqueCategories = [...new Set(assets.map(asset => asset.category).filter(Boolean))].sort();

  // Mobile card component — compact two-column grid tile
  const AssetCard = ({ asset }) => (
    <Card
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        borderTop: !asset.location ? `3px solid ${overdueColors.border}` : `3px solid ${accentBlue}`,
        bgcolor: !asset.location ? overdueColors.bg : paperBackground,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:active': {
          transform: 'scale(0.97)',
          transition: 'transform 0.1s ease-in-out',
        }
      }}
      onClick={() => navigate(`/assets/${asset.id}`)}
    >
      <CardContent sx={{ p: 1.5, pb: '8px !important', flex: 1 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', color: accentBlue, lineHeight: 1.2 }}>
          {asset.asset_number}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            mt: 0.5,
            mb: 0.75,
            fontSize: '0.78rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {asset.asset_description}
        </Typography>

        {asset.category && (
          <Chip
            label={asset.category}
            size="small"
            sx={{
              backgroundColor: pmTypeBackground,
              color: pmTypeText,
              border: '1px solid #2196f3',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: 20,
              mb: 0.5,
            }}
          />
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: !asset.location ? '#e65100' : 'text.secondary',
            fontSize: '0.7rem',
            mt: 0.25,
          }}
        >
          {asset.location || 'No location'}
        </Typography>

        {asset.open_work_orders > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: overdueColors.text,
              fontSize: '0.7rem',
              fontWeight: 600,
              mt: 0.25,
            }}
          >
            {asset.open_work_orders} open WO{asset.open_work_orders !== 1 ? 's' : ''}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const getLocationCounts = () => {
    const counts = { all: assets.length };
    const unassignedCount = assets.filter(a => !a.location).length;
    counts.not_assigned = unassignedCount;
    locations.forEach(location => {
      counts[location.name] = assets.filter(a => a.location === location.name).length;
    });
    return counts;
  };

  const locationCounts = getLocationCounts();

  const getUniqueCategoriesForLocation = () => {
    let filtered = assets;
    if (selectedTab === 'not_assigned') {
      filtered = assets.filter(a => !a.location);
    } else if (selectedTab !== 'all') {
      filtered = assets.filter(a => a.location === selectedTab);
    }
    return [...new Set(filtered.map(asset => asset.category).filter(Boolean))].sort();
  };

  const uniqueCategoriesForLocation = getUniqueCategoriesForLocation();

  const filteredAssets = assets.filter((asset) => {
    if (selectedTab === 'not_assigned') {
      if (asset.location) return false;
    } else if (selectedTab !== 'all' && asset.location !== selectedTab) {
      return false;
    }

    if (filterCategory && asset.category !== filterCategory) {
      return false;
    }

    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (asset.asset_number && asset.asset_number.toLowerCase().includes(searchLower)) ||
      (asset.asset_description && asset.asset_description.toLowerCase().includes(searchLower)) ||
      (asset.category && asset.category.toLowerCase().includes(searchLower)) ||
      (asset.location && asset.location.toLowerCase().includes(searchLower)) ||
      (asset.manufacturer && asset.manufacturer.toLowerCase().includes(searchLower)) ||
      (asset.model && asset.model.toLowerCase().includes(searchLower)) ||
      (asset.serial_number && asset.serial_number.toLowerCase().includes(searchLower)) ||
      (asset.department && asset.department.toLowerCase().includes(searchLower))
    );
  });

  const sortedAssets = [...filteredAssets].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    if (orderBy === 'asset_number') {
      const aIsNumeric = /^\d+$/.test(aVal);
      const bIsNumeric = /^\d+$/.test(bVal);

      if (aIsNumeric && bIsNumeric) {
        const comparison = parseInt(aVal) - parseInt(bVal);
        return order === 'asc' ? comparison : -comparison;
      } else if (aIsNumeric) {
        return order === 'asc' ? -1 : 1;
      } else if (bIsNumeric) {
        return order === 'asc' ? 1 : -1;
      }
    }

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (order === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  return (
    <Box>
      {/* Sticky header area - stays fixed while assets scroll */}
      <Box ref={stickyHeaderRef} sx={{
        position: 'sticky',
        top: 64,
        zIndex: 10,
        backgroundColor: pageBackground,
        mx: -3,
        px: 3,
        pt: 0.5,
        pb: 1,
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ color: accentBlue, fontWeight: 600 }}>Assets</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {hasRole('administrator', 'supervisor') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setFormData({});
                setOpenDialog(true);
              }}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: gradientPrimaryHover,
                },
              }}
            >
              {isMobile ? 'Add' : 'Add Asset'}
            </Button>
          )}
          {hasRole('administrator', 'supervisor') && (
            <>
              <IconButton
                onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                sx={{
                  border: `1px solid ${navyBlue}`,
                  color: accentBlue,
                  '&:hover': { backgroundColor: tableRowHover },
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
                PaperProps={{
                  sx: { borderRadius: 2, minWidth: 180 }
                }}
              >
                {hasRole('administrator') && (
                  <MenuItem onClick={() => { setMoreMenuAnchor(null); setOpenImportDialog(true); }}>
                    <UploadIcon sx={{ mr: 1.5, color: accentBlue }} /> Import
                  </MenuItem>
                )}
                {hasRole('administrator') && (
                  <MenuItem onClick={() => { setMoreMenuAnchor(null); setOpenExportDialog(true); }}>
                    <DownloadIcon sx={{ mr: 1.5, color: accentBlue }} /> Export
                  </MenuItem>
                )}
                <MenuItem onClick={() => { setMoreMenuAnchor(null); setOpenManageCategories(true); }}>
                  <CategoryIcon sx={{ mr: 1.5, color: accentBlue }} /> Manage Categories
                </MenuItem>
                <MenuItem onClick={() => { setMoreMenuAnchor(null); setOpenManageLocations(true); }}>
                  <LocationOnIcon sx={{ mr: 1.5, color: accentBlue }} /> Manage Locations
                </MenuItem>
                <MenuItem onClick={() => { setMoreMenuAnchor(null); setOpenRenumberDialog(true); }}>
                  <AutoFixHighIcon sx={{ mr: 1.5, color: accentBlue }} /> Bulk Renumber
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ mb: 2, p: isMobile ? 1.5 : 2, borderRadius: 2, border: `1px solid ${borderColor}` }}>
        <TextField
          fullWidth
          size={isMobile ? 'small' : 'medium'}
          placeholder="Search assets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={`All (${locationCounts.all})`}
            size={isMobile ? 'small' : 'medium'}
            onClick={() => {
              setSelectedTab('all');
              setSelectedAssets([]);
            }}
            sx={{
              fontWeight: 600,
              backgroundColor: selectedTab === 'all' ? navyBlue : 'transparent',
              color: selectedTab === 'all' ? 'white' : navyBlue,
              border: `1.5px solid ${navyBlue}`,
              '&:hover': {
                backgroundColor: selectedTab === 'all' ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
              },
            }}
          />
          {locationCounts.not_assigned > 0 && (
            <Chip
              label={`Not Assigned (${locationCounts.not_assigned})`}
              size={isMobile ? 'small' : 'medium'}
              onClick={() => {
                setSelectedTab('not_assigned');
                setSelectedAssets([]);
              }}
              sx={{
                fontWeight: 600,
                backgroundColor: selectedTab === 'not_assigned' ? '#f57c00' : 'transparent',
                color: selectedTab === 'not_assigned' ? 'white' : '#f57c00',
                border: '1.5px solid #f57c00',
                '&:hover': {
                  backgroundColor: selectedTab === 'not_assigned' ? '#ef6c00' : 'rgba(245, 124, 0, 0.08)',
                },
              }}
            />
          )}
          {locations.map((location) => (
            <Chip
              key={location.id}
              label={`${location.name} (${locationCounts[location.name] || 0})`}
              size={isMobile ? 'small' : 'medium'}
              onClick={() => {
                setSelectedTab(selectedTab === location.name ? 'all' : location.name);
                setSelectedAssets([]);
              }}
              sx={{
                fontWeight: 600,
                backgroundColor: selectedTab === location.name ? navyBlue : 'transparent',
                color: selectedTab === location.name ? 'white' : navyBlue,
                border: `1.5px solid ${navyBlue}`,
                '&:hover': {
                  backgroundColor: selectedTab === location.name ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                },
              }}
            />
          ))}
        </Box>
        {uniqueCategoriesForLocation.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            <Chip
              label="All Categories"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => setFilterCategory('')}
              sx={{
                fontWeight: 600,
                backgroundColor: !filterCategory ? navyBlue : 'transparent',
                color: !filterCategory ? 'white' : navyBlue,
                border: `1.5px solid ${navyBlue}`,
                '&:hover': {
                  backgroundColor: !filterCategory ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                },
              }}
            />
            {uniqueCategoriesForLocation.map((category) => (
              <Chip
                key={category}
                label={category}
                size={isMobile ? 'small' : 'medium'}
                onClick={() => setFilterCategory(filterCategory === category ? '' : category)}
                sx={{
                  fontWeight: 600,
                  backgroundColor: filterCategory === category ? navyBlue : 'transparent',
                  color: filterCategory === category ? 'white' : navyBlue,
                  border: `1.5px solid ${navyBlue}`,
                  '&:hover': {
                    backgroundColor: filterCategory === category ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                  },
                }}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {sortedAssets.length} of {assets.length} assets
          </Typography>
          {(searchTerm || filterCategory || selectedTab !== 'all') && (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('');
                setSelectedTab('all');
              }}
              sx={{ color: accentBlue }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>
      </Box>

      {/* Bulk Operations Toolbar */}
      {selectedAssets.length > 0 && hasRole('administrator', 'supervisor') && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            background: gradientPrimary,
            color: 'white',
            position: 'sticky',
            top: 64,
            zIndex: 1100,
            boxShadow: 6,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedAssets.length} asset(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="warning"
                onClick={handleBulkChangeLocation}
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Change Location
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDelete}
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Delete Selected
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {isMobile ? (
        <Box>
          {sortedAssets.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="textSecondary">No assets found</Typography>
            </Paper>
          ) : (
            <Grid container spacing={1.5}>
              {sortedAssets.map((asset) => (
                <Grid item xs={6} key={asset.id}>
                  <AssetCard asset={asset} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'visible' }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table stickyHeader sx={{
              '& .MuiTableHead-root .MuiTableCell-root': {
                top: `${stickyHeaderHeight}px`,
                backgroundColor: navyBlue,
                color: 'white',
              },
            }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {hasRole('administrator', 'supervisor') && (
                    <TableCell padding="checkbox" sx={{ backgroundColor: navyBlue }}>
                      <Checkbox
                        indeterminate={selectedAssets.length > 0 && selectedAssets.length < sortedAssets.length}
                        checked={sortedAssets.length > 0 && selectedAssets.length === sortedAssets.length}
                        onChange={handleSelectAll}
                        sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('asset_number') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'asset_number'}
                        direction={orderBy === 'asset_number' ? order : 'asc'}
                        onClick={() => handleRequestSort('asset_number')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Asset Number
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('asset_description') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'asset_description'}
                        direction={orderBy === 'asset_description' ? order : 'asc'}
                        onClick={() => handleRequestSort('asset_description')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Description
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('category') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'category'}
                        direction={orderBy === 'category' ? order : 'asc'}
                        onClick={() => handleRequestSort('category')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('location') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'location'}
                        direction={orderBy === 'location' ? order : 'asc'}
                        onClick={() => handleRequestSort('location')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('manufacturer') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'manufacturer'}
                        direction={orderBy === 'manufacturer' ? order : 'asc'}
                        onClick={() => handleRequestSort('manufacturer')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Manufacturer
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('model') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'model'}
                        direction={orderBy === 'model' ? order : 'asc'}
                        onClick={() => handleRequestSort('model')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Model
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('serial_number') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'serial_number'}
                        direction={orderBy === 'serial_number' ? order : 'asc'}
                        onClick={() => handleRequestSort('serial_number')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Serial Number
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('installation_date') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'installation_date'}
                        direction={orderBy === 'installation_date' ? order : 'asc'}
                        onClick={() => handleRequestSort('installation_date')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Installation Date
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('department') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'department'}
                        direction={orderBy === 'department' ? order : 'asc'}
                        onClick={() => handleRequestSort('department')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Department
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('current_meter_reading') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'current_meter_reading'}
                        direction={orderBy === 'current_meter_reading' ? order : 'asc'}
                        onClick={() => handleRequestSort('current_meter_reading')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Meter Reading
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('last_maintenance_date') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'last_maintenance_date'}
                        direction={orderBy === 'last_maintenance_date' ? order : 'asc'}
                        onClick={() => handleRequestSort('last_maintenance_date')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Last Maintenance
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {hasRole('administrator', 'supervisor') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAssets.map((asset, index) => (
                  <TableRow
                    key={asset.id}
                    hover
                    onClick={() => navigate(`/assets/${asset.id}`)}
                    sx={{
                      backgroundColor: !asset.location ? overdueColors.bg : (index % 2 === 0 ? paperBackground : tableRowAlt),
                      '&:hover': {
                        backgroundColor: !asset.location ? overdueColors.bg : tableRowHover
                      },
                      cursor: 'pointer',
                    }}
                  >
                    {hasRole('administrator', 'supervisor') && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedAssets.includes(asset.id)}
                          onChange={() => handleSelectAsset(asset.id)}
                        />
                      </TableCell>
                    )}
                    {isColumnVisible('asset_number') && (
                      <TableCell sx={{ fontWeight: 600, color: accentBlue }}>{asset.asset_number}</TableCell>
                    )}
                    {isColumnVisible('asset_description') && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: accentBlue,
                            fontWeight: 'bold',
                          }}
                        >
                          {asset.asset_description}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('category') && (
                      <TableCell>
                        {asset.category && (
                          <Chip
                            label={asset.category}
                            size="small"
                            sx={{
                              backgroundColor: pmTypeBackground,
                              color: pmTypeText,
                              border: '1px solid #2196f3',
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('location') && (
                      <TableCell>
                        {asset.location ? (
                          asset.location
                        ) : (
                          <Chip
                            label="No Location"
                            size="small"
                            sx={{
                              backgroundColor: overdueColors.bg,
                              color: overdueColors.text,
                              border: `1px solid ${overdueColors.border}`,
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible('manufacturer') && <TableCell>{renderEditableCell(asset, 'manufacturer')}</TableCell>}
                    {isColumnVisible('model') && <TableCell>{renderEditableCell(asset, 'model')}</TableCell>}
                    {isColumnVisible('serial_number') && <TableCell>{renderEditableCell(asset, 'serial_number')}</TableCell>}
                    {isColumnVisible('installation_date') && (
                      <TableCell>
                        {asset.installation_date ? new Date(asset.installation_date).toLocaleDateString() : '-'}
                      </TableCell>
                    )}
                    {isColumnVisible('department') && <TableCell>{asset.department || '-'}</TableCell>}
                    {isColumnVisible('current_meter_reading') && <TableCell>{asset.current_meter_reading || 0}</TableCell>}
                    {isColumnVisible('last_maintenance_date') && (
                      <TableCell>
                        {asset.last_maintenance_date ? new Date(asset.last_maintenance_date).toLocaleDateString() : '-'}
                      </TableCell>
                    )}
                    {hasRole('administrator', 'supervisor') && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(asset.id)} title="Delete Asset">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Asset Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setFormData({}); setError(''); }} maxWidth="md" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {formData.id ? 'Edit Asset' : 'Add Asset'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Asset Number" required
                value={formData.asset_number || ''}
                onChange={(e) => setFormData({ ...formData, asset_number: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Description" required
                value={formData.asset_description || ''}
                onChange={(e) => setFormData({ ...formData, asset_description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  label="Location"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.name}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Manufacturer"
                value={formData.manufacturer || ''}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Model"
                value={formData.model || ''}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Serial Number"
                value={formData.serial_number || ''}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Department"
                value={formData.department || ''}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
          {/* Desktop buttons inside content */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
              <Button
                onClick={() => { setOpenDialog(false); setFormData({}); }}
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                size="medium"
                sx={{
                  background: gradientPrimary,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                }}
              >
                {formData.id ? 'Update' : 'Create'}
              </Button>
            </Box>
          )}
          {/* Spacer for mobile sticky buttons */}
          {isMobile && <Box sx={{ height: 80 }} />}
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              onClick={() => { setOpenDialog(false); setFormData({}); }}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              size="large"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {formData.id ? 'Update' : 'Create'}
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)} maxWidth="sm" fullWidth>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Export Assets to Excel
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={exportCategory}
                  onChange={(e) => setExportCategory(e.target.value)}
                  label="Category"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                Export Type
              </Typography>
              <ToggleButtonGroup
                value={exportType}
                exclusive
                onChange={(e, newValue) => newValue && setExportType(newValue)}
                fullWidth
                sx={{ mt: 1 }}
              >
                <ToggleButton
                  value="summary"
                  sx={{
                    borderRadius: '20px 0 0 20px',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: navyBlue,
                      color: 'white',
                      '&:hover': { backgroundColor: '#2c4a6e' },
                    },
                  }}
                >
                  Summary (Visible Columns)
                </ToggleButton>
                <ToggleButton
                  value="detailed"
                  sx={{
                    borderRadius: '0 20px 20px 0',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: navyBlue,
                      color: 'white',
                      '&:hover': { backgroundColor: '#2c4a6e' },
                    },
                  }}
                >
                  Detailed (All Fields)
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={() => setOpenExportDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button
              onClick={handleExportToExcel}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Export
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={openManageCategories} onClose={() => { setOpenManageCategories(false); setError(''); }} maxWidth="sm" fullWidth>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Manage Categories
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="New Category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddCategory();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleAddCategory}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Add
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
            Existing Categories ({uniqueCategories.length})
          </Typography>
          {uniqueCategories.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
              No categories yet. Add one above.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {uniqueCategories.map((category) => {
                const assetCount = assets.filter(a => a.category === category).length;
                return (
                  <Paper key={category} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2, border: `1px solid ${borderColor}` }}>
                    {editingCategory === category ? (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          defaultValue={category}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameCategory(category, e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value !== category) {
                              handleRenameCategory(category, e.target.value);
                            } else {
                              setEditingCategory(null);
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button size="small" onClick={() => setEditingCategory(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{category}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {assetCount} asset{assetCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setEditingCategory(category)} title="Rename" sx={{ color: accentBlue }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setCategoryToDelete(category)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button onClick={() => setOpenManageCategories(false)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={Boolean(categoryToDelete)} onClose={() => setCategoryToDelete(null)}>
        <Box sx={{ background: `linear-gradient(135deg, #f44336 0%, #d32f2f 100%)`, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Delete Category?
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete the category "{categoryToDelete}"?
          </Typography>
          <Typography color="textSecondary" sx={{ mt: 1 }}>
            This will remove the category from {assets.filter(a => a.category === categoryToDelete).length} asset(s).
            The assets will not be deleted.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={() => setCategoryToDelete(null)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button onClick={() => handleDeleteCategory(categoryToDelete)} color="error" variant="contained" sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manage Locations Dialog */}
      <Dialog open={openManageLocations} onClose={() => { setOpenManageLocations(false); setError(''); }} maxWidth="sm" fullWidth>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Manage Locations
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="New Location"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddLocation();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleAddLocation}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Add
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
            Existing Locations ({locations.length})
          </Typography>
          {locations.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
              No locations yet. Add one above.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {locations.map((location) => {
                const assetCount = assets.filter(a => a.location === location.name).length;
                return (
                  <Paper key={location.id} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2, border: `1px solid ${borderColor}` }}>
                    {editingLocation === location.id ? (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          defaultValue={location.name}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameLocation(location.id, e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value !== location.name) {
                              handleRenameLocation(location.id, e.target.value);
                            } else {
                              setEditingLocation(null);
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button size="small" onClick={() => setEditingLocation(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{location.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {assetCount} asset{assetCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setEditingLocation(location.id)} title="Rename" sx={{ color: accentBlue }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setLocationToDelete(location)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button onClick={() => setOpenManageLocations(false)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Location Confirmation Dialog */}
      <Dialog open={Boolean(locationToDelete)} onClose={() => setLocationToDelete(null)}>
        <Box sx={{ background: `linear-gradient(135deg, #f44336 0%, #d32f2f 100%)`, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Delete Location?
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete the location "{locationToDelete?.name}"?
          </Typography>
          <Typography color="textSecondary" sx={{ mt: 1 }}>
            {locationToDelete && assets.filter(a => a.location === locationToDelete.name).length > 0 ? (
              <>Cannot delete this location because {assets.filter(a => a.location === locationToDelete.name).length} asset(s) are assigned to it. Please reassign or delete those assets first.</>
            ) : (
              <>This location will be permanently deleted.</>
            )}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={() => setLocationToDelete(null)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteLocation(locationToDelete.id)}
              color="error"
              variant="contained"
              disabled={locationToDelete && assets.filter(a => a.location === locationToDelete.name).length > 0}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bulk Renumber Dialog */}
      <Dialog open={openRenumberDialog} onClose={() => { setOpenRenumberDialog(false); setError(''); }} maxWidth="md" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Bulk Renumber Assets
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Renumbering will affect {sortedAssets.length} assets in the current view (filtered by: {selectedTab !== 'all' ? selectedTab : 'All'}).
          </Alert>

          <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
            Renumbering Method
          </Typography>
          <ToggleButtonGroup
            value={renumberMode}
            exclusive
            onChange={(e, newValue) => newValue && setRenumberMode(newValue)}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton
              value="sequential"
              sx={{
                borderRadius: '20px 0 0 20px',
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: navyBlue,
                  color: 'white',
                  '&:hover': { backgroundColor: '#2c4a6e' },
                },
              }}
            >
              Sequential
            </ToggleButton>
            <ToggleButton
              value="find_replace"
              sx={{
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: navyBlue,
                  color: 'white',
                  '&:hover': { backgroundColor: '#2c4a6e' },
                },
              }}
            >
              Find & Replace
            </ToggleButton>
            <ToggleButton
              value="prefix_suffix"
              sx={{
                borderRadius: '0 20px 20px 0',
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: navyBlue,
                  color: 'white',
                  '&:hover': { backgroundColor: '#2c4a6e' },
                },
              }}
            >
              Prefix/Suffix
            </ToggleButton>
          </ToggleButtonGroup>

          {renumberMode === 'sequential' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Starting Number"
                  type="number"
                  value={renumberStart}
                  onChange={(e) => setRenumberStart(e.target.value)}
                  helperText="Assets will be numbered sequentially from this number"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Prefix (optional)"
                  value={renumberPrefix}
                  onChange={(e) => setRenumberPrefix(e.target.value)}
                  placeholder="e.g., FAC-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Suffix (optional)"
                  value={renumberSuffix}
                  onChange={(e) => setRenumberSuffix(e.target.value)}
                  placeholder="e.g., -2024"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}

          {renumberMode === 'find_replace' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Find Text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  placeholder="Text to find"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Replace With"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replacement text"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}

          {renumberMode === 'prefix_suffix' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Prefix"
                  value={renumberPrefix}
                  onChange={(e) => setRenumberPrefix(e.target.value)}
                  placeholder="e.g., NEW-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Suffix"
                  value={renumberSuffix}
                  onChange={(e) => setRenumberSuffix(e.target.value)}
                  placeholder="e.g., -V2"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              onClick={generateRenumberPreview}
              variant="outlined"
              sx={{ borderColor: navyBlue, color: accentBlue, borderRadius: 2, textTransform: 'none' }}
            >
              Generate Preview
            </Button>
          </Box>

          {renumberPreview.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                Preview (first 10 assets):
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: tableRowAlt }}>
                      <TableCell sx={{ fontWeight: 600 }}>Current Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>New Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renumberPreview.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.oldNumber}</TableCell>
                        <TableCell><strong style={{ color: accentBlue }}>{item.newNumber}</strong></TableCell>
                        <TableCell>{item.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={() => setOpenRenumberDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyRenumbering}
              variant="contained"
              disabled={renumberPreview.length === 0}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Apply Renumbering
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Import Assets Dialog */}
      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Import Assets
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
            Import Mode
          </Typography>
          <ToggleButtonGroup
            value={importMode}
            exclusive
            onChange={(e, newValue) => {
              if (newValue) {
                setImportMode(newValue);
                setImportFile(null);
                setImportPreview([]);
                setImportError('');
              }
            }}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton
              value="new"
              sx={{
                borderRadius: '20px 0 0 20px',
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: navyBlue,
                  color: 'white',
                  '&:hover': { backgroundColor: '#2c4a6e' },
                },
              }}
            >
              New Assets
            </ToggleButton>
            <ToggleButton
              value="renumber"
              sx={{
                borderRadius: '0 20px 20px 0',
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: navyBlue,
                  color: 'white',
                  '&:hover': { backgroundColor: '#2c4a6e' },
                },
              }}
            >
              Renumber Existing
            </ToggleButton>
          </ToggleButtonGroup>

          {importMode === 'new' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload an Excel or CSV file with columns: <strong>Asset Number</strong> (required) and <strong>Asset Description</strong> or <strong>Asset Name</strong> (required).
              <br />Optional columns: Category, Location, Manufacturer, Model, Serial Number, Department, Installation Date, Purchase Date
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSampleTemplate}
                  sx={{ color: accentBlue }}
                >
                  Download Sample Template
                </Button>
              </Box>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload an Excel or CSV file with columns: <strong>old_asset_number</strong> and <strong>new_asset_number</strong>
            </Alert>
          )}

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <input
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              id="import-file-input"
              type="file"
              onChange={handleImportFile}
            />
            <label htmlFor="import-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{ borderColor: navyBlue, color: accentBlue, borderRadius: 2, textTransform: 'none' }}
              >
                Choose File
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </Box>

          {importPreview.length > 0 && (
            <Box>
              {importMode === 'renumber' ? (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                    Preview ({importPreview.filter(u => u.found).length} valid, {importPreview.filter(u => !u.found).length} not found):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: tableRowAlt }}>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Current Number</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>New Number</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importPreview.map((item, index) => (
                          <TableRow key={index} sx={{ bgcolor: item.found ? 'inherit' : '#ffebee' }}>
                            <TableCell>
                              {item.found ? (
                                <Chip label="Found" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #4caf50' }} />
                              ) : (
                                <Chip label="Not Found" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #f44336' }} />
                              )}
                            </TableCell>
                            <TableCell>{item.oldNumber}</TableCell>
                            <TableCell><strong style={{ color: accentBlue }}>{item.newNumber}</strong></TableCell>
                            <TableCell>{item.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                    Preview ({importPreview.filter(a => a.valid && !a.exists && !a.duplicateInFile).length} will be imported, {importPreview.filter(a => a.exists).length} already exist in database, {importPreview.filter(a => a.duplicateInFile).length} duplicates in file, {importPreview.filter(a => !a.valid).length} invalid):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: tableRowAlt }}>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Asset Number</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importPreview.map((item, index) => (
                          <TableRow key={index} sx={{
                            bgcolor: item.exists ? '#fff3e0' : (item.duplicateInFile ? '#ffebee' : (!item.valid ? '#ffebee' : '#e8f5e9'))
                          }}>
                            <TableCell>
                              {item.exists ? (
                                <Chip label="Exists in DB" size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ff9800' }} />
                              ) : item.duplicateInFile ? (
                                <Chip label="Duplicate in File" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #f44336' }} />
                              ) : (!item.valid ? (
                                <Chip label="Invalid" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #f44336' }} />
                              ) : (
                                <Chip label="New" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #4caf50' }} />
                              ))}
                            </TableCell>
                            <TableCell><strong style={{ color: accentBlue }}>{item.asset_number}</strong></TableCell>
                            <TableCell>{item.asset_description}</TableCell>
                            <TableCell>{item.category || '-'}</TableCell>
                            <TableCell>{item.location || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setOpenImportDialog(false);
                setImportFile(null);
                setImportPreview([]);
                setImportError('');
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyImport}
              variant="contained"
              disabled={importMode === 'renumber' ?
                importPreview.filter(u => u.found).length === 0 :
                importPreview.filter(a => a.valid && !a.exists).length === 0
              }
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Apply Updates ({importPreview.filter(u => u.found).length} assets)
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={openBulkDialog} onClose={() => { setOpenBulkDialog(false); setError(''); }} maxWidth="sm" fullWidth>
        <Box sx={{ background: `linear-gradient(135deg, ${bulkAction === 'delete' ? '#f44336' : navyBlue} 0%, ${bulkAction === 'delete' ? '#d32f2f' : '#2c4a6e'} 100%)`, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {bulkAction === 'delete' ? 'Delete Assets' : 'Change Location'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {bulkAction === 'delete' ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Are you sure you want to delete {selectedAssets.length} asset(s)?
              This will also delete all related work orders, PM schedules, and documents. This action cannot be undone.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>
                Select a new location for {selectedAssets.length} selected asset(s):
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Location</InputLabel>
                <Select
                  value={bulkLocation}
                  onChange={(e) => setBulkLocation(e.target.value)}
                  label="Location"
                  sx={{ borderRadius: 2 }}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.name}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={() => setOpenBulkDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button
              onClick={executeBulkAction}
              variant="contained"
              color={bulkAction === 'delete' ? 'error' : 'primary'}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              {bulkAction === 'delete' ? 'Delete' : 'Change Location'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
